import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { CompanyParametersBO, OnlineReceiversItem, OnlineReceiversQuery, SkylabConfig } from '@skylab-shared/business';
import { RepositorySaveResponse } from '@skylab-shared/core';
import { AccountingOnlineReceiverModel, CompanyParametersModel } from '@skylab-shared/dataModel';

import {
    CompanyParametersRepository,
    CompanyParametersRepositoryBase
} from '@skylab-angular-shared/business';
import {
    CommandBus, EventBus,
    EventBusBase, L10n,
    L10nBase, RootContext,
    RootContextBase, Weaver,
    WeaverBase
} from '@skylab-angular-shared/core';
import { ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, DialogState, FocusByDirective, FormContainerComponent, PasswordComponentIns, SelectValueAccessorDirective, ToastService, ValidateDirective, ValidationSourceDirective } from '@skylab-angular-shared/ui';

import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DictionaryDialogComponentBase, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

interface OnlineReceiverDialogItem {
    id: string;
    name: string;
    channelId: string;
    textStyle?: string;
}

interface DocumentRecipientViewModel extends DictionaryDialogViewModelBase<CompanyParametersBO> {
    password: string | null;
    onlineReceiver: OnlineReceiverDialogItem | null;
    onlineReceivers: OnlineReceiverDialogItem[];
    noAvailableReceiversUrl: string;
    onPasswordChange: () => void;
    onOnlineReceiverChange: () => void;
}

@Component({
    selector: 'skl-online-receiver-dialog',
    templateUrl: './online-receiver-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, FocusByDirective, SelectValueAccessorDirective, FormsModule, ValidateDirective, NgClass, PasswordComponentIns, ButtonComponent, TranslatePipe]
})
export class OnlineReceiverDialogComponent extends DictionaryDialogComponentBase<
    CompanyParametersModel,
    CompanyParametersBO,
    CompanyParametersRepositoryBase,
    DocumentRecipientViewModel
> {
    public constructor(
        @Inject(CompanyParametersRepository) companyParametersRepositoryBase: CompanyParametersRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        dialogService: DialogInsService,
        toastService: ToastService,
        commandBus: CommandBus,
    ) {
        super(
            companyParametersRepositoryBase,
            toastService,
            l10n,
            weaver,
            rootContext,
            commandBus,
            eventBus,
            dialogService
        );
    }

    protected override getInitialState(): DocumentRecipientViewModel {
        return {
            ...super.getInitialState(),
            password: null,
            onlineReceiver: null,
            onlineReceivers: [],
            noAvailableReceiversUrl: SkylabConfig.onlineReceiversConfigurationForumUrl,
        };
    }

    protected override async preloadDataAsync(): Promise<void> {
        await Promise.all([this.localContext.preload(OnlineReceiversQuery)]);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();

        const receivers = (this.viewModel.onlineReceivers = (this.localContext
            .getPreloaded(OnlineReceiversQuery) || [])
            .map((r: OnlineReceiversItem) => this.mapOnlineReceiver(r)));

        let receiver = this.viewModel.bo.model.accountingOnlineReceiver;
        if (receiver && receiver.channelId && !receivers.find(r => r.channelId === receiver?.channelId)) {
            receivers.push({
                id: receiver.id,
                name: receiver.name,
                channelId: receiver.channelId,
                textStyle: ''
            });
        }

        if (receivers.length > 0) {
            receivers.sort((r1, r2) => Number(r2.channelId) - Number(r1.channelId));
            if (!receiver) {
                receiver = this.viewModel.bo.model.accountingOnlineReceiver = new AccountingOnlineReceiverModel();
            }

            receivers[0].textStyle = 'text--bold';
            if (receiver.channelId) {
                this.viewModel.onlineReceiver = receivers.find(r => r.channelId === receiver?.channelId) || null;
            } else {
                this.viewModel.onlineReceiver = receivers[0];
            }
            this.viewModel.onOnlineReceiverChange = () => {
                if (this.viewModel.onlineReceiver && receiver) {
                    receiver.id = this.viewModel.onlineReceiver.id;
                    receiver.channelId = this.viewModel.onlineReceiver.channelId;
                    receiver.name = this.viewModel.onlineReceiver.name;
                }
            };
            this.viewModel.onOnlineReceiverChange();

            this.viewModel.password = receiver.password;
            if (!this.viewModel.password) {
                this.viewModel.password = null;
            }
            this.viewModel.onPasswordChange = () => {
                if (receiver) {
                    receiver.password = this.viewModel.password || '';
                }
            };
            this.viewModel.onPasswordChange();
        }
    }

    protected override async provideBusinessObjectAsync(): Promise<CompanyParametersBO> {
        try {
            return await this.repo.loadAsync(void 0 as any, this.localContext);
        } catch (err) {
            this.logger.error(err);
            throw err;
        }
    }

    private mapOnlineReceiver(r: OnlineReceiversItem): OnlineReceiverDialogItem {
        const companyName = r.companyName
            ? r.companyName
            : this.l10n._T('scenes.configuration.shared.online-receiver.dialog.form.name.lack-of-company-name');
        const instanceName = r.instanceName ? r.instanceName : '';
        const name = r.name ? `(${r.name})` : '';

        return {
            id: r.id,
            channelId: r.channelId,
            name: `${companyName} - ${instanceName} ${name}`,
            textStyle: ''
        };
    }

    protected override onSaveSuccess(_?: RepositorySaveResponse): void {
        this.dialogService.close<string>({ state: DialogState.Confirm, data: this.viewModel.bo.model.accountingOnlineReceiver?.channelId });
    }
}
